import React, { memo, useEffect, useRef } from 'react';
import targetSvgUrl from '../../look_for_target.svg';
import * as CSS from './TargetImagePromptUI.scss';
import { useDataStore, useUserStore } from '@/zustand';
import { getAllUsedImageTrackerIds } from '@/components/hoc/hocUtils';
import Carousel from '@/components/r3f-components/components/Carousel/Carousel';
import { useGetTargetLostBehaviour } from '../hooks';
import { IActionCategory } from '@/components/r3f-components/component-data-structure';
import { getTranslation, ITranslationKey } from '@/translations';
import { Jute } from '@/index';

interface IParentProps {
	forceEnabledImageUrl: string | null | undefined;
	isVisible: boolean;
	onGrabnGoClick?: () => unknown;
}

const TargetNotVisiblePrompt: React.FunctionComponent<IParentProps> = ({ forceEnabledImageUrl, isVisible, onGrabnGoClick }) => {
	const targetLostBehaviour = useGetTargetLostBehaviour();
	const isGrabAndGoEnabled = useUserStore(state => state.isGrabNGoEnabled as boolean);
	const isGrabAndGoActive = useUserStore(state => state.isGrabNGoActive as boolean);
	const trackingImageDataDict = useDataStore((state) => state.trackingImageDict) ?? {};
	const componentsById = useDataStore((state) => state.componentsById);
	const carouselRef = useRef<Carousel>(undefined);
	const trackingImgRef = useRef<HTMLImageElement>(document.createElement('img'));
	const flexDivRef = useRef<HTMLDivElement>(document.createElement('div'))
	const containerDivRef = useRef<HTMLDivElement>(document.createElement('div'));
	const textAndButtonContainerRef = useRef<HTMLDivElement>(document.createElement('div'));
	const targetImageDisplayDivRef = useRef<HTMLDivElement>(document.createElement('div'));
	const grabAndGoBtnRef = useRef<HTMLButtonElement>(document.createElement('button'))

	const isTargetLostBehviourGrabAndGo = targetLostBehaviour?.type === IActionCategory.showArContentScreenRelative;

	useEffect(() => {
		const canvasDiv = document.getElementById('zapparCanvas');
		if (!canvasDiv) return;

		carouselRef.current = new Carousel(undefined, {duration: 2500, pauseDuration: 5000});
		carouselRef.current.reset();
		containerDivRef.current.id = 'target-prompt-container';
		containerDivRef.current.classList.add(CSS.Container, CSS.Hidden);
		canvasDiv.appendChild(containerDivRef.current);

		flexDivRef.current.className = CSS.FlexContainer;
		containerDivRef.current.appendChild(flexDivRef.current);
		trackingImgRef.current.className = CSS.TrkImg;

		trackingImgRef.current.addEventListener('load', () => {
			if (trackingImgRef.current.width === 1) trackingImgRef.current.style.border = 'none';
		})

		targetImageDisplayDivRef.current.className = CSS.TrackingImageDisplayContainer;
		flexDivRef.current.appendChild(targetImageDisplayDivRef.current);

		const actionContainerDiv = document.createElement('div');
		actionContainerDiv.className = CSS.SVGContainer;
		flexDivRef.current.appendChild(actionContainerDiv);

		const phoneContainerdiv = document.createElement('div');
		phoneContainerdiv.className = CSS.PhoneContainer;

		const svgImg = document.createElement('img');
		svgImg.src = targetSvgUrl;
		phoneContainerdiv.appendChild(svgImg);
		actionContainerDiv.appendChild(phoneContainerdiv);

		const span = document.createElement('span');
		span.className = CSS.Text;
		span.innerText = getTranslation(ITranslationKey.pointCamToTargetImg);

		textAndButtonContainerRef.current.className = CSS.TextAndButtonContainer;
		textAndButtonContainerRef.current.appendChild(span);

		grabAndGoBtnRef.current.className = CSS.GrabAndGoButton;
		textAndButtonContainerRef.current.appendChild(grabAndGoBtnRef.current);
		grabAndGoBtnRef.current.style.visibility = 'hidden';
		grabAndGoBtnRef.current.style.pointerEvents = 'all';

		actionContainerDiv.appendChild(textAndButtonContainerRef.current)

		if (isTargetLostBehviourGrabAndGo && onGrabnGoClick) {
			grabAndGoBtnRef.current.addEventListener('click', onGrabnGoClick);
			grabAndGoBtnRef.current.style.visibility = 'visible';
		}

		return () => {
			carouselRef.current?.dispose();
			canvasDiv.removeChild(containerDivRef.current);
			if (containerDivRef.current.contains(flexDivRef.current)) containerDivRef.current.removeChild(flexDivRef.current);
			if (onGrabnGoClick) grabAndGoBtnRef.current.removeEventListener('click', onGrabnGoClick);
		};
	}, []);

	useEffect(() => {
		const allusedImageTrackerIds = getAllUsedImageTrackerIds(componentsById);
		const trackingImageUrls = allusedImageTrackerIds.map(id => trackingImageDataDict[id]?.imageUrl).filter(url => !!url);
		if (!trackingImageUrls) return;
		carouselRef.current?.setTrackingImageUrls(trackingImageUrls)
	}, [componentsById, trackingImageDataDict])

	useEffect(() => {
		if (isGrabAndGoActive) {
			if (containerDivRef.current.contains(flexDivRef.current)) containerDivRef.current.removeChild(flexDivRef.current);
			containerDivRef.current.style.pointerEvents = 'none';
			containerDivRef.current.style.background = 'linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 15%)'
			containerDivRef.current.style.justifyContent = 'flex-start';
			containerDivRef.current.style.alignItems = 'flex-end';
			if (textAndButtonContainerRef.current.contains(grabAndGoBtnRef.current)) {
				grabAndGoBtnRef.current.remove();
				containerDivRef.current.prepend(grabAndGoBtnRef.current)
			}
			grabAndGoBtnRef.current.style.margin = '21px';
			grabAndGoBtnRef.current.style.padding = '16px 18px';
			return;
		}
		containerDivRef.current.style.removeProperty('pointer-events');
		containerDivRef.current.style.removeProperty('background');
		containerDivRef.current.style.removeProperty('justify-content');
		containerDivRef.current.style.removeProperty('align-items');
		containerDivRef.current.style.removeProperty('padding');
		grabAndGoBtnRef.current.style.removeProperty('margin');
		grabAndGoBtnRef.current.style.removeProperty('padding');
		if (!containerDivRef.current.contains(flexDivRef.current)) containerDivRef.current.prepend(flexDivRef.current);
		if (containerDivRef.current.contains(grabAndGoBtnRef.current)) {
			grabAndGoBtnRef.current.remove()
			textAndButtonContainerRef.current.appendChild(grabAndGoBtnRef.current)
		}
	}, [isGrabAndGoActive])

	// Switch between carousel and image
	useEffect(() => {
		if (!forceEnabledImageUrl) {
			if (targetImageDisplayDivRef.current.contains(trackingImgRef.current)) targetImageDisplayDivRef.current.removeChild(trackingImgRef.current);	
			if (!targetImageDisplayDivRef.current.contains(carouselRef.current!.container)) {
				carouselRef.current?.start();
				targetImageDisplayDivRef.current.prepend(carouselRef.current!.container)
			};
			return;
		};
		/* eslint-disable @typescript-eslint/no-non-null-assertion */
		if (!trackingImgRef.current) return;
		if (targetImageDisplayDivRef.current.contains(carouselRef.current!.container)) {
			targetImageDisplayDivRef.current.removeChild(carouselRef.current!.container);
			carouselRef.current?.reset();
		};
		trackingImgRef.current.src = forceEnabledImageUrl || '';
		if (!targetImageDisplayDivRef.current.contains(trackingImgRef.current)) targetImageDisplayDivRef.current.prepend(trackingImgRef.current);
	}, [forceEnabledImageUrl]);

	useEffect(() => {
		if (isGrabAndGoActive) {
			grabAndGoBtnRef.current.textContent = getTranslation(ITranslationKey.close);
			return;
		}
		if (isGrabAndGoEnabled) { 
			grabAndGoBtnRef.current.textContent = getTranslation(ITranslationKey.pinToScreenOn);
			return;
		}
		grabAndGoBtnRef.current.textContent = getTranslation(ITranslationKey.pinToScreenOff);
	}, [isGrabAndGoEnabled, isGrabAndGoActive])

	useEffect(() => {
		if (!containerDivRef?.current) return;
		carouselRef.current?.reset();
		/* eslint-disable @typescript-eslint/no-non-null-assertion */
		if (!isVisible) {
			grabAndGoBtnRef.current.style.visibility = 'hidden';
			containerDivRef.current!.classList.add(CSS.Hidden);
			return;
		};
		containerDivRef.current!.classList.remove(CSS.Hidden);

		if (isTargetLostBehviourGrabAndGo) {
			grabAndGoBtnRef.current.style.visibility = 'visible';
		}
		carouselRef.current?.start();

		if (Jute.isDevRelease && document.getElementsByClassName(CSS.Text)[0]) {
			document.getElementsByClassName(CSS.Text)[0].textContent = getTranslation(ITranslationKey.pointCamToTargetImg);
		}
	}, [isVisible, containerDivRef]);

	return null;
};
export default memo(TargetNotVisiblePrompt);